import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { TuiTable } from '@taiga-ui/addon-table';
import { TuiPagination } from '@taiga-ui/kit';
import { PatientService } from '../../services/patient.service';
import { PatientList } from '../../models/patient';
import { PatientRowComponent } from '../patient-row/patient-row.component';
import { ReactiveFormsModule } from '@angular/forms';
import { TuiButton } from '@taiga-ui/core';
import { User } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { ActivatedRoute } from '@angular/router';
import { PrescriptionFormService } from '../../services/prescription-form.service';
import { CommonService } from '../../services/common.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-patient-list',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PatientRowComponent,
    TuiTable,
    AsyncPipe,
    TuiPagination,
    TuiButton,
  ],
  templateUrl: './patient-list.component.html',
  styleUrl: './patient-list.component.scss',
})
export class PatientListComponent implements OnInit, OnChanges {
  @Input() dosActiviteList: string[] = [];
  @Input() nom: string | undefined;
  @Input() dosEcheanceMin: Date | undefined;
  @Input() dosEcheanceMax: Date | undefined;
  @Input() dosEcheanceInitiales: boolean = false;
  @Input() page: number = 0;
  @Output() onChangeDePage = new EventEmitter<number>(); // Événement personnalisé

  private route = inject(ActivatedRoute);

  ppcOrdonnanceSignMass: number[] = [];

  public etatPush: undefined | boolean | 'loading';

  user: User | null = null;
  private readonly commonService = inject(CommonService);
  protected readonly authService = inject(AuthService);
  private readonly prescriptionFormService = inject(PrescriptionFormService);
  private readonly patientService = inject(PatientService);
  private readonly userService = inject(UserService);

  public patientList0: WritableSignal<PatientList | undefined> =
    signal(undefined);

  private subscriptionChargementListePatient: Subscription | undefined;

  changementDePage(page: number) {
    this.onChangeDePage.emit(page);
  }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.patientList0.set(undefined);

    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.subscriptionChargementListePatient = this.userService
          .pullPatientList(
            params['id'],
            undefined,
            this.nom,
            this.dosActiviteList,
            this.dosEcheanceMin,
            this.dosEcheanceMax,
            this.dosEcheanceInitiales,
          )
          .subscribe({
            next: (patientList: PatientList) => {
              this.patientList0.set(patientList);
            },
          });
      } else {
        this.subscriptionChargementListePatient = this.patientService
          .pullList(
            undefined,
            this.nom,
            this.dosActiviteList,
            this.dosEcheanceMin,
            this.dosEcheanceMax,
            this.dosEcheanceInitiales,
          )
          .subscribe({
            next: (patientList: PatientList) => {
              this.patientList0.set(patientList);
            },
          });
      }
    });
  }

  ngOnChanges(): void {
    if (this.subscriptionChargementListePatient) {
      this.subscriptionChargementListePatient.unsubscribe();
    }
    this.patientList0.set(undefined);
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.subscriptionChargementListePatient = this.userService
          .pullPatientList(
            params['id'],
            this.page + 1,
            this.nom,
            this.dosActiviteList,
            this.dosEcheanceMin,
            this.dosEcheanceMax,
            this.dosEcheanceInitiales,
          )
          .subscribe({
            next: (patientList: PatientList) => {
              this.patientList0.set(patientList);
            },
          });
      } else {
        this.subscriptionChargementListePatient = this.patientService
          .pullList(
            this.page + 1,
            this.nom,
            this.dosActiviteList,
            this.dosEcheanceMin,
            this.dosEcheanceMax,
            this.dosEcheanceInitiales,
          )
          .subscribe({
            next: (patientList: PatientList) => {
              this.patientList0.set(patientList);
            },
          });
      }
    });
  }

  submit(): void {
    if (this.user?.id) {
      this.etatPush = 'loading';
      this.prescriptionFormService
        .pushMass({
          ppc_ordonnance: this.ppcOrdonnanceSignMass,
        })
        .subscribe({
          next: (data) => {
            if (!data || (data instanceof Array && data.length == 0)) {
              this.commonService.showSuccessAlert(
                'Signature en masse réussie intégralement.',
              );
            } else {
              let messageDErreur =
                'Les prescriptions doivent être traitées individuellement pour les patients suivants : ';
              data.forEach((patientId: number) => {
                let patient = this.patientList0()?.data.find(
                  (test) => test.id == patientId,
                );

                if (patient) {
                  messageDErreur += `${patient.nom} ${patient.prenom} ; `;
                }
              });
              this.commonService.showErrorAlert(messageDErreur);
            }
            this.ppcOrdonnanceSignMass = [];
            this.ngOnChanges();
            this.etatPush = true;
          },
          error: (error) => {
            this.commonService.showErrorAlert('Signature en masse échouée.');
            this.etatPush = false;
          },
        });
    }
  }

  clickSignMass(id: number) {
    if (this.ppcOrdonnanceSignMass.includes(id)) {
      this.ppcOrdonnanceSignMass = this.ppcOrdonnanceSignMass.filter(
        (testId) => testId != id,
      );
    } else {
      this.ppcOrdonnanceSignMass.push(id);
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptionChargementListePatient) {
      this.subscriptionChargementListePatient.unsubscribe();
    }
  }
}
